import { ApiRef, createApiRef } from '@backstage/core-plugin-api';
import { CatalogServicePartner } from '../models/CatalogServicePartner';
import {
  CustomFormFieldLookupResponse,
  CustomFormFieldValidationResponse,
} from '../models/CustomFormField';
import { FeatureFlag } from '../models/FeatureFlag';
import { Product } from '../models/Product';
import { ProductHistoryItem } from '../models/ProductHistory';
import { ProductType } from '../models/ProductType';
import { ProductUsageCount } from '../models/ProductUsageCount';
import { OrderNumberLookupResult } from '../models/OrderNumberLookup';
import { ProjectLink } from '../models/ProjectLink';
import { ProvisioningOrder } from '../models/ProvisioningOrder';
import { SupervisorProductCost } from '../models/SupervisorProductCost';
import { UserLicenseStatus } from '../models/UserLicenseStatus';
import { UserSearchItem } from '../models/UserSearchItem';
import { IconDisplayMode } from '../utils/urls';

export interface GetProductsOptions {
  type: ProductType;
}

export type CatalogType = 'Tool' | 'UserLicense';

export interface CatalogSnapshot {
  products: Product[];
  servicePartners: CatalogServicePartner[];
}

export interface SaveShoppingCartOptions {
  key: string;
  projectKey: string;
  value: string;
}

export interface ValidateShoppingCartOptions {
  key: string;
  projectKey?: string;
  value: string;
  branchName?: string;
}

export type ProjectPayload = Record<string, unknown>;

export interface SaveProjectOptions {
  project: ProjectPayload;
  isNewProject: boolean;
}

export interface UpdateProjectOwnershipOptions {
  projectKey: string;
  ownerUserId?: string;
  coOwnerUserId?: string;
}

export interface UpdateProjectUserRolesOptions {
  projectKey: string;
  roles: unknown;
}

export interface UpdateServicePartnerDataOptions {
  projectKey: string;
  servicePartnerData: unknown;
}

export interface UserRefPayload {
  userId: string;
  displayName: string;
  email?: string;
  isOwner?: boolean;
  isCoOwner?: boolean;
}

export type UserPayload = Record<string, unknown>;

export interface UserAuthorizationPayload {
  user: UserPayload;
  isAuthorized: boolean;
  isEmailVerificationActive: boolean;
  isSupervisor: boolean;
}

export interface UserProjectRefPayload {
  projectKey: string;
  displayName: string;
  visited?: number;
  helpDesk?: boolean;
}

export interface UpsertUserOptions {
  user: UserPayload;
  signal?: AbortSignal;
}

export interface LicenseCustomFormData {
  id: string;
  data: unknown;
}

export interface OrderUserLicenseOptions {
  catalogProductRefId: string;
  catalogVariantRefId?: string;
  ou?: string;
  costCenter?: string;
  internalOrderNumber?: string;
  customFormData?: LicenseCustomFormData[];
  validateOnly?: boolean;
}

export interface UpdateUserLicenseOptions {
  licenseId: string;
  catalogProductRefId: string;
  catalogVariantRefId?: string;
  ou?: string;
  costCenter?: string;
  internalOrderNumber?: string;
}

export interface CancelUserLicenseOptions {
  licenseId: string;
  force?: boolean;
}

export interface SearchUsersOptions {
  searchTerm: string;
  queryParams?: string;
  signal?: AbortSignal;
}

export interface OrderNumberLookupOptions {
  ou: string;
  internalOrderNumber: string;
  signal?: AbortSignal;
}

export interface ConvertShoppingCartOptions {
  projectKey: string;
  key: string;
  value: string;
}

export interface UpdateCustomFormDataOptions {
  customFormId: string;
  data: unknown;
  instanceId: string;
  projectKey?: string;
  justification?: string | null;
}

export interface UpdateServicePartnerCustomFormDataOptions {
  projectKey: string;
  name: string;
  id: string;
  data: unknown;
}

export interface CancelToolOptions {
  projectKey: string;
  toolId: string;
  force?: boolean;
}

export interface UpdateToolOptions {
  projectKey: string;
  tool: unknown;
  validateOnly?: boolean;
}

export interface CostCenterLookupOptions {
  ou: string;
  searchTerm: string;
  signal?: AbortSignal;
}

export interface BillingValidationOptions {
  plant: string;
  costCenter: string;
  co: string;
  signal?: AbortSignal;
}

export interface BillingValidationResult {
  errors: string[];
  warnings: string[];
}

export interface CustomFormFieldLookupOptions {
  servicePartner: string;
  customFormFieldId: string;
  filter?: string;
  signal?: AbortSignal;
}

export interface ValidateCustomFormFieldOptions extends CustomFormFieldLookupOptions {
  value: string;
}

export interface ResolveAssetUrlOptions {
  size?: number;
  mode?: IconDisplayMode;
}

export interface DeveloperCompassApi {
  getFeatureFlags(withTarget?: boolean): Promise<FeatureFlag[]>;

  getProducts(options: GetProductsOptions): Promise<Product[]>;

  getCatalog(options: GetProductsOptions): Promise<CatalogSnapshot>;

  getUserLicenseUsageCounts(): Promise<ProductUsageCount[]>;

  getToolUsageCounts(): Promise<ProductUsageCount[]>;

  getAverageProvisioningTimes(
    catalogType: CatalogType,
  ): Promise<Record<string, number>>;

  resolveAssetUrl(
    path: string,
    options?: ResolveAssetUrlOptions,
  ): Promise<string | undefined>;

  getShoppingCart(key: string): Promise<string | undefined>;

  saveShoppingCart(options: SaveShoppingCartOptions): Promise<void>;

  deleteShoppingCart(key: string): Promise<void>;

  validateShoppingCart(options: ValidateShoppingCartOptions): Promise<void>;

  getProject(projectKey: string): Promise<ProjectPayload | undefined>;

  saveProject(options: SaveProjectOptions): Promise<ProjectPayload | undefined>;

  getProjectProvisioningOrders(
    projectKey: string,
  ): Promise<ProvisioningOrder[]>;

  getToolProvisioningOrders(
    projectKey: string,
    toolId: string,
  ): Promise<ProvisioningOrder[]>;

  updateProjectOwnership(options: UpdateProjectOwnershipOptions): Promise<void>;

  updateProjectUserRoles(options: UpdateProjectUserRolesOptions): Promise<void>;

  updateServicePartnerData(
    options: UpdateServicePartnerDataOptions,
  ): Promise<boolean>;

  getUserRef(searchTerm: string): Promise<UserRefPayload | undefined>;

  updateProjectDataSelfAssessment(
    projectKey: string,
    data: unknown,
  ): Promise<void>;

  getProjectKeySuggestion(projectName: string): Promise<string | undefined>;

  updateToolCustomFormData(options: UpdateCustomFormDataOptions): Promise<void>;

  updateLicenseCustomFormData(
    options: UpdateCustomFormDataOptions,
  ): Promise<void>;

  updateServicePartnerCustomFormData(
    options: UpdateServicePartnerCustomFormDataOptions,
  ): Promise<void>;

  cancelTool(options: CancelToolOptions): Promise<void>;

  undoCancelTool(projectKey: string, toolId: string): Promise<void>;

  getProjectToolLinks(projectKey: string): Promise<ProjectLink[]>;

  updateTool(options: UpdateToolOptions): Promise<void>;

  convertShoppingCart(options: ConvertShoppingCartOptions): Promise<void>;

  validateBillingInformation(
    options: BillingValidationOptions,
  ): Promise<BillingValidationResult>;

  getCostCenterLookup(options: CostCenterLookupOptions): Promise<string[]>;

  getCompleteCostCenterLookup(
    options: CostCenterLookupOptions,
  ): Promise<string[]>;

  getCustomFormFieldLookup(
    options: CustomFormFieldLookupOptions,
  ): Promise<CustomFormFieldLookupResponse | undefined>;

  validateCustomFormField(
    options: ValidateCustomFormFieldOptions,
  ): Promise<CustomFormFieldValidationResponse | undefined>;

  getUser(userId: string): Promise<UserAuthorizationPayload | undefined>;

  upsertUser(
    options: UpsertUserOptions,
  ): Promise<UserAuthorizationPayload | undefined>;

  getUserProjectRef(
    projectKey: string,
  ): Promise<UserProjectRefPayload | undefined>;

  getUserLicenseProvisioningOrders(): Promise<ProvisioningOrder[]>;

  getUserLicenseLinks(): Promise<ProjectLink[]>;

  getUserLicenseHistory(licenseId: string): Promise<ProductHistoryItem[]>;

  getUserLicenseStatus(): Promise<UserLicenseStatus[]>;

  orderUserLicense(options: OrderUserLicenseOptions): Promise<void>;

  cancelUserLicense(options: CancelUserLicenseOptions): Promise<void>;

  undoCancelUserLicense(licenseId: string): Promise<void>;

  updateUserLicense(options: UpdateUserLicenseOptions): Promise<void>;

  cancelUserLicenseReview(userId: string, licenseId: string): Promise<void>;

  keepUserLicenseReview(userId: string, licenseId: string): Promise<void>;

  resetTeamMemberBudget(
    userId: string,
    licenseInstanceId: string,
  ): Promise<void>;

  getOrderNumberLookup(
    options: OrderNumberLookupOptions,
  ): Promise<OrderNumberLookupResult | undefined>;

  getOrderNumberCount(ou: string, signal?: AbortSignal): Promise<number>;

  confirmProvisioning(path: string, requestId: string): Promise<void>;

  verifyEmail(verificationCode: string): Promise<void>;

  searchUsers(options: SearchUsersOptions): Promise<UserSearchItem[]>;

  getServicePartners(): Promise<string[]>;

  getServicePartnerDetails(
    servicePartnerName: string,
  ): Promise<Record<string, unknown> | undefined>;

  getLicensesUnderSupervisor(
    catalogRefId?: string,
  ): Promise<SupervisorProductCost[]>;

  getToolsUnderSupervisor(): Promise<SupervisorProductCost[]>;

  getToolCostUnderSupervisor(
    catalogRefId: string,
  ): Promise<SupervisorProductCost | undefined>;
}

export const developerCompassApiRef: ApiRef<DeveloperCompassApi> =
  createApiRef<DeveloperCompassApi>({
    id: 'plugin.developer-compass.api',
  });
